import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActionMeta } from 'react-select';
import ReactAsyncSelect from 'react-select/async';

import { IOption } from '../Input/Input';
import { getSelectValue, getSelectValues, TMenuPosition } from '../Select/Select';
import { useTranslation } from "react-i18next";
import { ETranslation } from '../../../translations/translation-keys';

export type TAsyncFetchOption = (inputValue: string, signal?: AbortSignal) => Promise<IOption[]>;

interface IProps {
  value: string | string[];
  placeholder?: string;
  multiple?: boolean;
  disabled?: boolean;
  onChange: (value: string | string[]) => void;
  fetchOptions: TAsyncFetchOption | any;
  options?: IOption[];
  menuPosition?: TMenuPosition;
  searchMinLength?: number;
  hideControls?: boolean;
  returnObject?: boolean;
  debounceTime?: number;
  autoFocus?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
}

const AsyncSelect: React.FC<IProps> = ({
  value,
  onChange,
  placeholder,
  multiple,
  disabled,
  fetchOptions,
  options,
  menuPosition,
  searchMinLength,
  hideControls,
  returnObject,
  debounceTime = 350,
  autoFocus,
  onBlur,
}) => {
  const [selectValue, setSelectValue] = useState<IOption | IOption[] | undefined>();
  const minLength = useMemo(() => searchMinLength ? searchMinLength : 3, [searchMinLength]);
  const { t } = useTranslation();

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pendingResolveRef = useRef<((res: IOption[]) => void) | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (pendingResolveRef.current) {
        pendingResolveRef.current([]);
      }
    };
  }, []);

  useEffect(() => {
    if (value && (value.length || typeof value === 'number') && options) {
      if (multiple) {
        const values = (value as string[]).map(value => value.toString());
        setSelectValue(getSelectValues(options, values));
      } else {
        setSelectValue(getSelectValue(options, value.toString()))
      }
    } else {
      setSelectValue(undefined);
    }

  }, [value, options, multiple]);
  
  const changeHandler = (option: any, actionMeta: ActionMeta<any>) => {
    if (returnObject) {
      onChange(option || '');
      return;
    }
    if (multiple) {
      onChange((option as IOption[] | undefined)?.map(value => value.value) || '')
    } else {
      onChange((option as IOption | undefined)?.value || '');
    }
  }

  const promiseOptions = useCallback(
    (input: string) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      if (pendingResolveRef.current) {
        pendingResolveRef.current([]);
        pendingResolveRef.current = null;
      }

      if (input.length < minLength) {
        return Promise.resolve([]);
      }

      return new Promise<IOption[]>((resolve, reject) => {
        pendingResolveRef.current = resolve;

        timerRef.current = setTimeout(async () => {
          const controller = new AbortController();
          abortControllerRef.current = controller;

          try {
            const results = await fetchOptions(input, controller.signal);
            if (!controller.signal.aborted) {
              resolve(results || []);
            } else {
              resolve([]);
            }
          } catch (err: any) {
            if (
              controller.signal.aborted ||
              err?.name === "AbortError" ||
              err?.name === "CanceledError" ||
              err?.message === "Canceled"
            ) {
              resolve([]);
            } else {
              reject(err);
            }
          } finally {
            if (abortControllerRef.current === controller) {
              abortControllerRef.current = null;
            }
            if (pendingResolveRef.current === resolve) {
              pendingResolveRef.current = null;
            }
          }
        }, debounceTime);
      });
    },
    [debounceTime, fetchOptions, minLength]
  );

  const customStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    option: (provided: any) => ({ ...provided, color: '#2F2F2F' }),
    singleValue: (provided: any) => ({ ...provided, color: '#2F2F2F' }),
    input: (provided: any) => ({ ...provided, color: '#2F2F2F' }),
    multiValueLabel: (provided: any) => ({ ...provided, color: '#2F2F2F' }),
  };

  return (
    <ReactAsyncSelect
      defaultOptions={options}
      onChange={changeHandler}
      cacheOptions
      loadOptions={promiseOptions}
      placeholder={placeholder || t(ETranslation.REACT_SELECT_PLACEHOLDER)}
      isMulti={multiple}
      value={selectValue}
      isDisabled={disabled}
      autoFocus={autoFocus}
      onBlur={onBlur}
      menuPosition={menuPosition}
      menuPortalTarget={menuPosition === 'fixed' ? document.body : undefined}
      styles={customStyles}
      className="react-select-container"
      classNamePrefix="react-select"
      components={hideControls ? { DropdownIndicator: () => null, IndicatorSeparator: () => null, ClearIndicator: () => null } : {}}
      noOptionsMessage={({ inputValue }) => {
        const length = inputValue.length;
        if (length === 0) {
          return "Kirjoita hakuehto"
        } else if(length >= 1 && length < minLength) {
          return `Kirjoita vähintään ${minLength} merkkiä.`
        }
        return t(ETranslation.NOT_FOUND_ERROR);
      }}
      isClearable
    />
  );
};

export default AsyncSelect;
